import React, { useEffect, useState } from 'react';
import { getRouteComponent, getRoutePaths, getRedirects } from './routeRegistry';

// Tracks how far we've escalated recovery for the current load attempt. Stored
// in sessionStorage so it survives the reload but resets when the tab closes.
const RECOVERY_STAGE_KEY = 'routeRecoveryStage';

const isKnownRoute = (path, siteType) => {
  try {
    if (getRoutePaths(siteType).includes(path)) return true;
    return Object.values(getRedirects(siteType)).includes(path);
  } catch (_) {
    return false;
  }
};

const getStage = () => {
  try {
    return Number(sessionStorage.getItem(RECOVERY_STAGE_KEY)) || 0;
  } catch (_) {
    return 0;
  }
};

const setStage = (stage) => {
  try {
    sessionStorage.setItem(RECOVERY_STAGE_KEY, String(stage));
  } catch (_) {
    // ignore storage failures
  }
};

const clearRecoveryStage = () => {
  try {
    sessionStorage.removeItem(RECOVERY_STAGE_KEY);
  } catch (_) {
    // ignore storage failures
  }
};

/**
 * A registered route that fails to load is almost always a stale chunk left
 * over from a deploy (the cached index.html points at chunk hashes that no
 * longer exist), or occasionally corrupted local state. Try to self-heal:
 *   stage 0 → reload once (fetches fresh chunks, keeps the user's picks)
 *   stage 1 → clear localStorage and reload (recover from corrupted state)
 *   stage 2 → give up and show the error
 * Returns true if a recovery reload was triggered (so the caller should keep
 * showing the loading state instead of the error).
 */
const attemptRouteRecovery = (path, siteType) => {
  if (typeof window === 'undefined') return false;
  // Never wipe data for a genuinely unknown route — only self-heal real routes.
  if (!isKnownRoute(path, siteType)) return false;

  const stage = getStage();
  if (stage === 0) {
    setStage(1);
    window.location.reload();
    return true;
  }
  if (stage === 1) {
    setStage(2);
    try {
      localStorage.clear();
    } catch (_) {
      // ignore storage failures
    }
    window.location.reload();
    return true;
  }
  return false;
};

/**
 * Lazy route component that dynamically loads route components
 */
export const LazyRoute = ({ path, siteType, history }) => {
  const [Component, setComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadComponent = async () => {
      try {
        setLoading(true);
        setError(null);
        const LoadedComponent = await getRouteComponent(path, siteType);

        if (!isMounted) return;

        if (LoadedComponent) {
          clearRecoveryStage(); // success — reset so future failures can recover
          setComponent(() => LoadedComponent);
          setLoading(false);
        } else if (!attemptRouteRecovery(path, siteType)) {
          setError(new Error(`Route not found: ${path}`));
          setLoading(false);
        }
        // If recovery was triggered the page is reloading; keep showing Loading.
      } catch (err) {
        if (!isMounted) return;
        if (!attemptRouteRecovery(path, siteType)) {
          setError(err);
          setLoading(false);
        }
      }
    };

    loadComponent();

    return () => {
      isMounted = false;
    };
  }, [path, siteType]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500">Error loading route: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!Component) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-400">Route not found</p>
        </div>
      </div>
    );
  }

  return <Component history={history} />;
};
